import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Users, Navigation, MapPin, Clock, AlertTriangle, Wrench, ShieldCheck, Camera,
  BarChart3, ArrowRight, CheckCircle2,
} from "lucide-react";

const GROUPS = [
  {
    id: "dispatch",
    label: "Dispatch & Live Operations",
    items: [
      [Navigation, "Trip scheduling", "Create and schedule trips with pickup, drop-off and mobility needs."],
      [MapPin, "Live GPS visibility", "Track every vehicle on the live map with real-time trip status."],
      [Clock, "Late-trip prevention", "Automatic late-risk alerts flag at-risk trips before they slip."],
      [Users, "Driver reassignment", "Reassign trips on the fly when plans change."],
    ],
  },
  {
    id: "fleet",
    label: "Fleet & Driver Management",
    items: [
      [Users, "Driver rosters", "Manage drivers, ratings, duty status and credentials."],
      [Wrench, "Fleet maintenance", "Track service schedules, repairs and repair costs."],
      [BarChart3, "Vehicle roster", "Vehicle types, mileage, assignments and inspection status."],
    ],
  },
  {
    id: "safety",
    label: "Safety & Accountability",
    items: [
      [AlertTriangle, "Incident management", "Log, categorize, investigate and resolve incidents."],
      [Camera, "Trip evidence", "Retain documented proof for every ride."],
      [BarChart3, "Operational reporting", "Measure on-time performance and accountability."],
    ],
  },
  {
    id: "compliance",
    label: "Compliance",
    items: [
      [ShieldCheck, "Driver compliance", "Licenses, certifications and background checks."],
      [ShieldCheck, "Vehicle compliance", "Inspections, insurance and emissions tracking."],
    ],
  },
];

export default function FeaturesPage() {
  const navigate = useNavigate();
  return (
    <div data-testid="features-page">
      <div className="bg-slate-950 pt-36 pb-20 text-white">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-400">Features</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            Built for the realities of transportation operations
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            Every RideStamp capability exists to answer one question — can you account for every
            ride, every driver and every vehicle?
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-20 px-5 py-20 lg:px-8 lg:py-28">
        {GROUPS.map((g) => (
          <section key={g.id} id={g.id} className="scroll-mt-28">
            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-slate-200" />
              <h2 className="font-display text-2xl font-bold text-slate-900">{g.label}</h2>
              <span className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map(([Icon, t, d], i) => (
                <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/60"
                  data-testid={`feature-${g.id}-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
                </div>
              ))}
            </div>
          </section>
        ))}

        <div className="rounded-3xl bg-slate-50 p-10 text-center lg:p-16">
          <h2 className="font-display text-2xl font-bold text-slate-900 lg:text-3xl">See it with your own data</h2>
          <p className="mx-auto mt-3 max-w-xl text-slate-600">Request a walkthrough and we'll show RideStamp against a real transportation operation.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-6">
            {["Live GPS", "Late-risk alerts", "Compliance tracking", "Trip evidence"].map((t) => (
              <span key={t} className="flex items-center gap-2 text-sm font-semibold text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t}</span>
            ))}
          </div>
          <Button onClick={() => navigate("/request-demo")} data-testid="features-cta"
            className="mt-8 gap-2 rounded-full bg-blue-800 px-8 py-6 text-base font-semibold text-white hover:bg-blue-900">
            Request a Demo <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
